import type { ServiceProviderDuration } from "@/types/service";

interface DurationSource {
  duration: number;
  providerDurations?: ServiceProviderDuration[];
}

/**
 * Minutes `providerId` takes to perform the service: their override when the
 * service has one for them, otherwise the service's own duration.
 */
export function providerServiceDuration(
  service: DurationSource,
  providerId: string | null | undefined,
): number {
  if (!providerId) return service.duration;
  return (
    service.providerDurations?.find((item) => item.providerId === providerId)
      ?.duration ?? service.duration
  );
}

/**
 * The duration a newly placed service should take: `requestedMinutes` is what
 * the picker offered (the service default); a provider with their own time
 * for the service gets that instead. Anything else was chosen on purpose.
 */
export function placementDuration(
  service: DurationSource | undefined,
  providerId: string | null | undefined,
  requestedMinutes: number,
): number {
  if (!service || requestedMinutes !== service.duration)
    return requestedMinutes;
  return providerServiceDuration(service, providerId);
}

/** Shortest and longest time anyone takes: the default and every provider's own time. */
export function serviceDurationRange(service: DurationSource): {
  min: number;
  max: number;
} {
  const minutes = [
    service.duration,
    ...(service.providerDurations ?? []).map((item) => item.duration),
  ];
  return { min: Math.min(...minutes), max: Math.max(...minutes) };
}

export type DurationBound = keyof ReturnType<typeof serviceDurationRange>;

/**
 * A copy of the service (and its add-on services) at its shortest or longest
 * time, so any duration total can be worked out at both ends of the range.
 */
export function atDurationBound<T extends DurationSource>(
  service: T,
  bound: DurationBound,
): T {
  const next: T = {
    ...service,
    duration: serviceDurationRange(service)[bound],
  };
  const addons = (
    service as { addons?: { addonService?: DurationSource | null }[] | null }
  ).addons;
  if (Array.isArray(addons)) {
    (next as { addons?: unknown }).addons = addons.map((addon) =>
      addon.addonService
        ? { ...addon, addonService: atDurationBound(addon.addonService, bound) }
        : addon,
    );
  }
  return next;
}

/** "30" or "30–45", for labels that add their own unit. */
export function formatDurationRange(min: number, max: number): string {
  return max > min ? `${min}–${max}` : `${min}`;
}
